import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Trash2, Plus, Minus, ShoppingBag, ShoppingCart, MapPin, Tag, Truck, ChevronRight, Shield, Package, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { FullPageLoader } from '../components/MedicalLoader';

const Cart = () => {
  const { user, loading: authLoading } = useAuth();
  const { cart, loading: cartLoading, updateQuantity: updateCartQuantity, removeFromCart, fetchCart } = useCart();
  const navigate = useNavigate();
  const [similarProducts, setSimilarProducts] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    if (authLoading || cartLoading) return;
    fetchSimilarProducts(cart.items || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, authLoading, cartLoading]);

  const fetchSimilarProducts = async (cartItems = []) => {
    try {
      const response = await api.get('/products?limit=10');
      // Get IDs of products already in cart
      const cartProductIds = cartItems.map(item => item.product_id);
      // Filter out products that are already in the cart
      const filteredProducts = response.data.filter(
        product => !cartProductIds.includes(product.id)
      );
      setSimilarProducts(filteredProducts.slice(0, 4));
    } catch (error) {
      console.error('Error fetching similar products:', error);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    await updateCartQuantity(productId, newQuantity);
  };

  const removeItem = async (productId) => {
    await removeFromCart(productId);
  };

  const { addToCart } = useCart();

  const handleAddSimilarToCart = async (product) => {
    await addToCart(product, 1);
  };

  const calculateMRP = () => {
    return cart.items.reduce((total, item) => {
      const originalPrice = item.product?.original_price || item.product?.price || 0;
      return total + originalPrice * item.quantity;
    }, 0);
  };

  const calculateTotal = () => {
    return cart.items.reduce((total, item) => {
      return total + (item.product?.price || 0) * item.quantity;
    }, 0);
  };

  const calculateDiscount = () => {
    return calculateMRP() - calculateTotal();
  };

  const getCouponDiscount = () => {
    if (appliedCoupon) {
      return Math.round(calculateTotal() * 0.1); // 10% discount
    }
    return 0;
  };

  const getFinalAmount = () => {
    return calculateTotal() - getCouponDiscount();
  };

  const applyCoupon = () => {
    if (couponCode.toUpperCase() === 'SAVE10') {
      setAppliedCoupon({ code: 'SAVE10', discount: 10 });
      toast.success('Coupon applied! 10% off');
    } else if (couponCode.toUpperCase() === 'FIRST50') {
      setAppliedCoupon({ code: 'FIRST50', discount: 50 });
      toast.success('Coupon applied! ₹50 off');
    } else {
      toast.error('Invalid coupon code');
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.success('Coupon removed');
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    // If user is not logged in, redirect to login with checkout redirect
    if (!user) {
      toast.info('Please login to proceed to checkout');
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  if (cartLoading) {
    return <FullPageLoader text="Loading your cart..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="cart-page">
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {cart.items.length === 0 ? (
            <div className="max-w-2xl mx-auto" data-testid="empty-cart-message">
              {/* Empty Cart Card */}
              <div className="bg-white rounded-3xl border border-gray-100 shadow-lg overflow-hidden">
                {/* Gradient Header */}
                <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 px-8 py-12 text-center">
                  <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mx-auto flex items-center justify-center mb-6 shadow-lg">
                    <ShoppingCart className="h-12 w-12 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">Your Cart is Empty</h2>
                  <p className="text-purple-100 text-lg">Looks like you haven't added anything yet</p>
                </div>
                
                {/* Content Section */}
                <div className="p-8 text-center">
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <Truck className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-600 font-medium">Free Delivery</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-xl">
                      <ShieldCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-600 font-medium">Genuine Products</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <Package className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-600 font-medium">Easy Returns</p>
                    </div>
                  </div>
                  
                  <Link to="/products">
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all">
                      <ShoppingBag className="h-5 w-5 mr-2" />
                      Start Shopping
                    </Button>
                  </Link>
                  
                  <p className="text-gray-500 text-sm mt-6">
                    Explore our wide range of medical equipment
                  </p>
                </div>
              </div>
              
              {/* Featured Products Suggestion */}
              {similarProducts.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Popular Products</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {similarProducts.map((product) => (
                      <Link key={product.id} to={`/product/${product.id}`} className="group">
                        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                          <div className="aspect-square bg-gray-50 overflow-hidden">
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          </div>
                          <div className="p-3">
                            <h4 className="text-xs font-medium text-gray-900 line-clamp-2">{product.name}</h4>
                            <p className="text-sm font-semibold text-purple-600 mt-1">₹{product.price.toLocaleString()}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Side - Cart Items */}
              <div className="flex-1">
                {/* Items Count Header */}
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-900" data-testid="cart-title">
                    {cart.items.length} Item{cart.items.length > 1 ? 's' : ''} in cart
                  </h1>
                  <Link to="/products" className="text-purple-600 hover:text-purple-700 font-medium flex items-center text-xs sm:text-sm">
                    + Add More
                  </Link>
                </div>

                {/* Cart Items */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="cart-items-list">
                  {cart.items.map((item, index) => (
                    <div
                      key={item.product_id}
                      className={`p-3 sm:p-4 ${index !== cart.items.length - 1 ? 'border-b border-gray-100' : ''}`}
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      <div className="flex gap-3">
                        {/* Product Image */}
                        <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={item.product?.image}
                              alt={item.product?.name}
                              className="w-full h-full object-cover"
                              data-testid="cart-item-image"
                            />
                          </div>
                        </Link>
                        
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <Link to={`/product/${item.product_id}`}>
                            <h3 className="font-medium text-gray-900 hover:text-purple-600 transition-colors mb-1 line-clamp-2 text-xs sm:text-sm" data-testid="cart-item-name">
                              {item.product?.name}
                            </h3>
                          </Link>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm sm:text-lg font-semibold text-gray-900" data-testid="cart-item-price">
                              ₹{item.product?.price?.toLocaleString()}
                            </span>
                            {item.product?.discount_percentage > 0 && (
                              <span className="text-green-600 text-xs font-medium">
                                {item.product.discount_percentage}% OFF
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeItem(item.product_id)}
                          className="text-red-400 hover:text-red-500 transition-colors p-1 h-fit flex-shrink-0"
                          data-testid="remove-item-button"
                        >
                          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                      
                      {/* Quantity Controls - Below on Mobile */}
                      <div className="flex items-center justify-end mt-2 sm:mt-0">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="px-2 sm:px-3 py-1.5 sm:py-2 text-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:text-gray-400"
                            data-testid="decrease-quantity-button"
                          >
                            <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                          </button>
                          <span className="px-3 sm:px-4 py-1.5 sm:py-2 font-medium text-gray-900 min-w-[32px] sm:min-w-[40px] text-center text-sm" data-testid="cart-item-quantity">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            className="px-2 sm:px-3 py-1.5 sm:py-2 text-purple-600 hover:bg-purple-50"
                            data-testid="increase-quantity-button"
                          >
                            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* People Also Bought This */}
                {similarProducts.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900">People also bought</h2>
                      <Link to="/products" className="text-purple-600 hover:text-purple-700 text-xs sm:text-sm font-medium flex items-center">
                        View All <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Link>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 sm:gap-4">
                      {similarProducts.slice(0, 4).map((product) => (
                        <div key={product.id} className="bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                          <Link to={`/product/${product.id}`}>
                            <div className="aspect-square bg-gray-100 overflow-hidden">
                              <img 
                                src={product.image} 
                                alt={product.name}
                                className="w-full h-full object-cover hover:scale-105 transition-transform"
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=400&q=80'; }}
                              />
                            </div>
                          </Link>
                          <div className="p-2 sm:p-3">
                            <Link to={`/product/${product.id}`}>
                              <h3 className="text-[10px] sm:text-xs font-medium text-gray-900 line-clamp-2 mb-1 hover:text-purple-600 h-8 sm:h-auto">
                                {product.name}
                              </h3>
                            </Link>
                            <div className="flex items-center gap-1 mb-2">
                              <span className="text-xs sm:text-sm font-semibold text-gray-900">₹{product.price?.toLocaleString()}</span>
                            </div>
                            <Button 
                              onClick={() => handleAddSimilarToCart(product)}
                              className="w-full h-7 sm:h-8 text-[10px] sm:text-xs bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Payment Details */}
              <div className="lg:w-[340px] flex-shrink-0">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm lg:sticky lg:top-24" data-testid="order-summary">
                  {/* Delivery Address */}
                  <div className="p-3 sm:p-4 border-b border-gray-100">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-gray-900">Add delivery address</h3>
                          <Link to="/checkout" className="text-purple-600 text-sm font-medium">Add</Link>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Add address & get <span className="text-green-600 font-medium">free delivery</span> on your first order</p>
                      </div>
                    </div>
                  </div>

                  {/* Apply Coupon */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <Tag className="h-5 w-5 text-purple-600 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-gray-900">Apply Coupon</h3>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                    {!appliedCoupon ? (
                      <div className="flex gap-2 mt-3">
                        <Input
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          placeholder="Enter coupon code"
                          className="flex-1 h-9 text-sm"
                        />
                        <Button 
                          onClick={applyCoupon}
                          variant="outline" 
                          className="h-9 text-sm border-purple-200 text-purple-600 hover:bg-purple-50"
                        >
                          Apply
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-3 flex items-center justify-between bg-green-50 p-2 rounded-lg">
                        <span className="text-sm text-green-700 font-medium">{appliedCoupon.code} applied</span>
                        <button onClick={removeCoupon} className="text-red-500 text-xs">Remove</button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Try: SAVE10, FIRST50</p>
                  </div>

                  {/* Payment Details */}
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total MRP</span>
                        <span className="text-gray-900">₹{calculateMRP().toLocaleString()}</span>
                      </div>
                      
                      {calculateDiscount() > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Discount on MRP</span>
                          <span className="text-green-600">- ₹{calculateDiscount().toLocaleString()}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Coupon</span>
                        <span className="text-green-600">- ₹{getCouponDiscount()}</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Platform Fees</span>
                        <span className="text-gray-400 line-through mr-1">₹10</span>
                        <span className="text-gray-900">₹0</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 underline cursor-help">Handling Charges</span>
                        <span className="text-gray-900">₹0</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Delivery Charges</span>
                        <span className="text-green-600 font-medium">FREE</span>
                      </div>
                      
                      {(calculateDiscount() + getCouponDiscount()) > 0 && (
                        <div className="bg-green-50 p-3 rounded-lg flex justify-between">
                          <span className="text-green-700 font-medium text-sm">Total savings</span>
                          <span className="text-green-700 font-semibold">
                            ₹{(calculateDiscount() + getCouponDiscount()).toLocaleString()} ({Math.round(((calculateDiscount() + getCouponDiscount()) / calculateMRP()) * 100)}%)
                          </span>
                        </div>
                      )}
                      
                      <div className="border-t border-gray-100 pt-3 flex justify-between">
                        <span className="font-semibold text-gray-900">Amount to be paid</span>
                        <span className="font-bold text-xl text-gray-900" data-testid="total-amount">₹{getFinalAmount().toLocaleString()}*</span>
                      </div>
                      
                      <p className="text-[10px] text-gray-400">*Final amount may change due to batch changes in products</p>
                    </div>
                    
                    <Button
                      onClick={handleCheckout}
                      className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold mt-4 shadow-md"
                      data-testid="checkout-button"
                    >
                      Proceed
                    </Button>
                    
                    {/* Trust Badge */}
                    <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-600">
                      <Shield className="h-4 w-4 text-green-600" />
                      <span>100% Safe and Secure Payment</span>
                    </div>
                    
                    {/* Payment Icons */}
                    <div className="flex items-center justify-center gap-3 mt-3">
                      <img src="https://razorpay.com/assets/razorpay-glyph.svg" alt="Razorpay" className="h-6" />
                      <span className="text-xs font-semibold text-gray-400">GPay</span>
                      <span className="text-xs font-semibold text-blue-500">Paytm</span>
                      <span className="text-xs font-semibold text-purple-600">PhonePe</span>
                      <span className="text-xs font-semibold text-orange-500">UPI</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
